import { Button, Dropdown, Flex, Popover, Tooltip, Typography } from 'antd'
import type { ButtonProps } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Columns3, RotateCcw, Settings } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIconSizes } from 'src/hooks'

import { ColumnSettingsPanel } from './ColumnSettingsPanel'
import { useDraftColumnsMap } from './hooks'
import { useStyles } from './styles'
import type {
  ColumnSettingsAction,
  ColumnSettingsConfig,
  ColumnSettingsCustomization,
  ColumnSettingsHiddenGroup,
  ColumnStateMap
} from './types'

const CUSTOMIZE_KEY = 'customize-columns'

type Props<T> = {
  columns: ColumnsType<T>
  columnsMap: ColumnStateMap | undefined
  icons?: ColumnSettingsConfig['icons']
  hints?: ColumnSettingsConfig['hints']
  lockedKeys?: string[]
  hiddenGroups?: ColumnSettingsHiddenGroup[]
  actions?: ColumnSettingsAction[]
  customization?: ColumnSettingsCustomization
  size?: ButtonProps['size']
  onChange: (columnsMap: ColumnStateMap) => void
  onReset: () => void
}

export const ColumnSettings = <T,>({
  columns,
  columnsMap: committedMap,
  icons,
  hints,
  lockedKeys,
  hiddenGroups,
  actions,
  customization,
  size,
  onChange,
  onReset
}: Props<T>) => {
  const { t } = useTranslation()
  const { styles, theme } = useStyles()
  const iconSizes = useIconSizes()
  const [isOpen, setIsOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const {
    columnsMap,
    change: onEdit,
    discard
  } = useDraftColumnsMap(committedMap, onChange)

  const isPanelOpen = customization ? customization.isPanelOpen : isOpen
  const isCustomizing = customization?.isCustomizing ?? false
  const isActive = isPanelOpen || isMenuOpen || isCustomizing
  const triggerLabel = t(
    actions ? 'table_settings' : 'column_settings_configure'
  )

  const onOpenChange = (open: boolean) => {
    if (customization) {
      if (!customization.isCustomizing) {
        return
      }

      if (open) {
        customization.onOpenPanel()
      } else {
        customization.onClosePanel()
      }

      return
    }

    if (open && actions) {
      return
    }

    setIsOpen(open)
  }

  const trigger = (
    <Tooltip title={triggerLabel} open={isActive ? false : undefined}>
      <Button
        aria-label={triggerLabel}
        aria-expanded={isPanelOpen || isMenuOpen}
        aria-pressed={customization ? isCustomizing : undefined}
        icon={<Settings size={iconSizes.default} />}
        size={size}
        color={isActive ? 'primary' : 'default'}
        variant={isActive ? 'filled' : 'text'}
      />
    </Tooltip>
  )

  const header = (
    <Flex align="center" justify="space-between" gap="small">
      <Typography.Text strong>{t('column_settings_title')}</Typography.Text>

      {!customization && (
        <Button
          type="link"
          size="small"
          icon={<RotateCcw size={iconSizes.small} />}
          onClick={() => {
            discard()
            onReset()
          }}
        >
          {t('column_settings_reset')}
        </Button>
      )}
    </Flex>
  )

  const content = (
    <ColumnSettingsPanel
      columns={columns}
      columnsMap={columnsMap}
      icons={icons}
      hints={hints}
      lockedKeys={lockedKeys}
      hiddenGroups={hiddenGroups}
      onChange={onEdit}
    />
  )

  const panel = (
    <Popover
      open={isPanelOpen}
      trigger="click"
      placement="bottomRight"
      arrow={false}
      destroyOnHidden
      title={header}
      content={content}
      classNames={{ container: styles.panel }}
      styles={{
        container: {
          paddingBlock: `${theme.paddingSM}px 0`,
          paddingInline: 0
        },
        title: { paddingInline: theme.paddingSM }
      }}
      onOpenChange={onOpenChange}
    >
      {actions ? (
        <Dropdown
          open={isMenuOpen}
          disabled={isPanelOpen || isCustomizing}
          trigger={['click']}
          placement="bottomRight"
          menu={{
            items: [
              ...actions.map(({ key, label, icon }) => ({
                key,
                label,
                icon
              })),
              {
                key: CUSTOMIZE_KEY,
                icon: <Columns3 />,
                disabled: customization ? !customization.canStart : false,
                label: t('column_settings_customize')
              }
            ],
            onClick: ({ key }) => {
              if (key === CUSTOMIZE_KEY) {
                if (customization) {
                  customization.onCustomize()
                } else {
                  setIsOpen(true)
                }

                return
              }

              actions.find((action) => action.key === key)?.onClick()
            }
          }}
          onOpenChange={setIsMenuOpen}
        >
          {trigger}
        </Dropdown>
      ) : (
        trigger
      )}
    </Popover>
  )

  return customization?.bar ? (
    <>
      {panel}
      {customization.bar}
    </>
  ) : (
    panel
  )
}
